import React from 'react';

import {
  Button,
  Link as MuiLink,
  Stack,
  type Theme,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';

import { type useDashboardList } from '../hooks/useDashboardList';

import DashboardEmptyState from './DashboardEmptyState';
import { DashboardSectionShell } from './DashboardSectionShell';

type DashboardCardProps<TItem> = {
  title: string;
  titleAdornment?: React.ReactNode;
  actionLabel?: string;
  actionLink?: string;
  onActionClick?: () => void;
  queryResult: ReturnType<typeof useDashboardList<any, any, TItem>>;
  renderItem: (item: TItem, key: string | number) => React.ReactNode;
  emptyStateMessage: string;
  emptyStateVariant?: 'empty' | 'success';
  emptyStateActionLabel?: string;
  onEmptyStateAction?: () => void;
  customAction?: React.ReactNode;
  extraContent?: React.ReactNode;
  hideBadge?: boolean;
  anchorId?: string;
  highlighted?: boolean;
};

export const DashboardCard = <TItem,>({
  title,
  titleAdornment,
  actionLabel,
  actionLink,
  onActionClick,
  queryResult,
  renderItem,
  emptyStateMessage,
  emptyStateVariant,
  emptyStateActionLabel,
  onEmptyStateAction,
  customAction,
  extraContent,
  hideBadge,
  anchorId,
  highlighted,
}: DashboardCardProps<TItem>) => {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const {
    items,
    totalCount,
    hasMore,
    loadMore,
    loadMoreButtonRef,
    loading,
    isLoadingMore,
    error,
    setError,
  } = queryResult;

  const defaultAction =
    actionLabel != null && (actionLink != null || onActionClick != null) ? (
      <Button
        size="small"
        sx={{ fontWeight: 600 }}
        {...(actionLink != null
          ? { component: Link, to: actionLink }
          : { onClick: onActionClick })}
      >
        {actionLabel}
      </Button>
    ) : null;

  return (
    <>
      <DashboardSectionShell
        title={title}
        titleAdornment={titleAdornment}
        action={isMobile ? undefined : customAction ?? defaultAction}
        loading={loading}
        count={totalCount}
        expandable
        hideBadge={hideBadge}
        anchorId={anchorId}
        highlighted={highlighted}
        hasMoreItems={hasMore}
        loadMoreRef={loadMoreButtonRef}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      >
        {extraContent}
        {items.length === 0 ? (
          <DashboardEmptyState
            message={emptyStateMessage}
            variant={emptyStateVariant}
            action={
              emptyStateActionLabel && onEmptyStateAction ? (
                <MuiLink
                  component="button"
                  variant="body2"
                  onClick={onEmptyStateAction}
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    cursor: 'pointer',
                    ml: -0.5,
                  }}
                >
                  {emptyStateActionLabel}
                </MuiLink>
              ) : undefined
            }
          />
        ) : (
          <Stack spacing={1}>
            {items.map((item, index) =>
              renderItem(item, (item as { id: string | number }).id ?? index),
            )}
          </Stack>
        )}
      </DashboardSectionShell>
      <MutationErrorSnackbar error={error} onClose={() => setError(null)} />
    </>
  );
};
